import type { EnhancedApiConfigurationStep } from '#api/enhancements/enhancedTmsApiTypes'
import type { ConfigurationStep } from '#features/Configuration/ConfigurationStep'
import type { AnyConfigurationViewId } from '#pages/Configuration/components/ConfigurationNavigation'

// RequestConfigurationStep and SystemConfigurationStep have 'no sufficient overlap' on purpose
// this makes it harder accidentally comparing the two, passing one or the other as argument
export type RequestConfigurationStep = ConfigurationStep & { __requestConfigurationStepBrand: never }
export type SystemConfigurationStep = EnhancedApiConfigurationStep | 'unknown'

export type ConfigurationStage = 'PendingStep' | ConfigurationStep | 'SummaryStep'
export type ViewConfigurationStep = ('PendingStep' | ConfigurationStep | 'SummaryStep') & { __viewConfigurationStepBrand: never }

export const makeRequestConfigurationStep = (step: ConfigurationStep) => step as RequestConfigurationStep
export const makeViewConfigurationStage = (stage: ConfigurationStage) => stage as ViewConfigurationStep

export const stepForStage = (stage: ConfigurationStage) =>
  stage === 'PendingStep' || stage === 'GeneralStep' ? makeRequestConfigurationStep('GeneralStep') : makeRequestConfigurationStep('DetailedStep')

export const defaultViewForStage = (stage: ConfigurationStage): AnyConfigurationViewId => {
  switch (stage) {
    case 'PendingStep':
      return 'configuration/step-0'
    case 'GeneralStep':
      return 'configuration/step-1'
    case 'DetailedStep':
      return 'configuration/step-2'
    case 'SummaryStep':
      return 'configuration/step-3'
  }
}

export function stageForView(view: AnyConfigurationViewId): ViewConfigurationStep | undefined {
  switch (view) {
    case 'configuration/copy':
      return undefined
    case 'configuration/new':
    case 'configuration/step-0':
    case 'configuration/step-0/history':
    case 'configuration/step-0/landing':
      return makeViewConfigurationStage('PendingStep')
    case 'configuration/step-1':
    case 'configuration/step-1/history':
      return makeViewConfigurationStage('GeneralStep')
    case 'configuration/step-2':
    case 'configuration/step-2/history':
    case 'configuration/step-2/compare':
    case 'configuration/step-2/print':
    case 'configuration/step-2/technical-data':
    case 'configuration/step-2/build-verification':
    case 'configuration/step-2/optional-equipment':
    case 'configuration/step-2/tire-editor':
      return makeViewConfigurationStage('DetailedStep')
    case 'configuration/step-3/compare':
    case 'configuration/step-3':
    case 'configuration/step-3/technical-data':
    case 'configuration/step-3/build-verification':
    case 'configuration/step-3/changes':
    case 'configuration/step-3/submitted':
    case 'configuration/step-3/history':
    case 'configuration/step-3/optional-equipment':
      return makeViewConfigurationStage('SummaryStep')
    case 'configuration/error':
      return makeViewConfigurationStage('PendingStep')
  }
}

export const stepForView = (view: AnyConfigurationViewId) => {
  const stage = stageForView(view)
  if (!stage) {
    return
  }
  return stepForStage(stage)
}
